import { useState, useRef, useCallback } from 'react';
import useTipClamp from './useTipClamp';

/**
 * HoverBar — a drop-in replacement for a bar <div> that shows a floating
 * tooltip above it on mouse hover (desktop) or tap (mobile). Preserves the
 * exact layout of the bar it replaces: same className, same style (height
 * percentage), same data-testid — just adds `position: relative` so the
 * tooltip can be absolutely positioned above.
 *
 * Used across the app for consistent bar-chart interaction:
 *   DivGrowthTab, DripCalcTab, PortfolioDripModal, CompareModal.
 *
 * Props:
 *   label      — string shown in the tooltip (e.g. "TRIN 2024: $1.98")
 *   className  — classes applied to the bar <div>
 *   style      — inline style applied to the bar (usually { height, background })
 *   testId     — data-testid passed through
 *   isDark     — optional; themes the tooltip chip
 *
 * Behavior:
 *   - Desktop: show on mouseenter, hide on mouseleave
 *   - Mobile:  show on touchstart, auto-hide after 1.5s
 *
 * Horizontal clamping via `useTipClamp` ensures the tooltip never
 * overflows the nearest clipping ancestor (modal, chart card) or the
 * viewport, so the last-bar-in-modal case never clips on desktop or
 * mobile.
 */
export default function HoverBar({ label, className = '', style, testId, isDark = false }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const tipRef = useRef(null);
  const shiftX = useTipClamp(tipRef, open, [label]);

  const show = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);
  const touchShow = useCallback((e) => {
    e.stopPropagation();
    show();
    timerRef.current = setTimeout(() => setOpen(false), 1500);
  }, [show]);

  const tipBg = isDark ? 'bg-zinc-800' : 'bg-zinc-900';
  const arrowColor = isDark ? '#27272a' : '#18181b';

  return (
    <div
      className={`relative ${className}`}
      style={style}
      data-testid={testId}
      onMouseEnter={show}
      onMouseLeave={hide}
      onTouchStart={touchShow}
    >
      {open && label && (
        <div
          ref={tipRef}
          className={`pointer-events-none absolute left-1/2 -top-2 ${tipBg} text-white text-[11px] font-mono font-semibold px-2 py-1 rounded-md whitespace-nowrap shadow-lg z-20 select-none`}
          style={{ transform: `translate(calc(-50% + ${shiftX}px), -100%)` }}
          data-testid="hover-bar-tooltip"
        >
          {label}
          <span
            className="absolute top-full -mt-px border-[5px] border-transparent"
            style={{
              // Keep the arrow pointing at the bar's center even when
              // the tooltip body has been shifted left/right to avoid
              // overflow: left = 50% - shift puts the arrow back on
              // the bar's vertical midline.
              left: `calc(50% - ${shiftX}px)`,
              transform: 'translateX(-50%)',
              borderTopColor: arrowColor,
            }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
